import React, { useState } from "react";
import { toast } from "sonner";
import { Flask } from "@phosphor-icons/react";
import { adminApi, getErrorMessage } from "@/lib/api";
import { Card, Stat, btnPrimary, selectCls } from "../ui";
import { DECISIONS } from "./parts";

const OUT_COLS = [["approved", "Approved"], ["declined", "Declined"]];

function Matrix({ m }) {
  return (
    <table className="w-full text-[0.7rem] mt-3" data-testid="wi-calibration-matrix">
      <thead className="text-neutral-500"><tr><th className="text-left py-1 font-medium">AI suggested ↓ / Human decided →</th>{OUT_COLS.map(([k, l]) => <th key={k} className="text-right py-1 font-medium">{l}</th>)}</tr></thead>
      <tbody>
        {Object.entries(DECISIONS).map(([k, d]) => (
          <tr key={k} className="border-t border-neutral-900"><td className="py-1.5 text-neutral-300">{d.label}</td>
            {OUT_COLS.map(([o]) => <td key={o} className="py-1.5 text-right tabular-nums text-neutral-200">{m?.[k]?.[o] ?? 0}</td>)}</tr>
        ))}
      </tbody>
    </table>
  );
}

export function CalibrationCard({ ov, onStarted }) {
  const [n, setN] = useState(25);
  const [busy, setBusy] = useState(false);
  const cal = ov.calibration;
  const job = ov.calibration_job;
  const running = job?.status === "running";
  const run = async () => {
    setBusy(true);
    try {
      const { data } = await adminApi.post("/admin/wi/assess/calibration", { n: Number(n) });
      if (data.started) toast.success(`Testing the AI on ${data.claims} past claims`); else toast.info(data.reason);
      onStarted();
    } catch (e) { toast.error(getErrorMessage(e)); } finally { setBusy(false); }
  };
  return (
    <Card title="Test against past decisions" icon={Flask} testId="wi-calibration-card">
      <p className="text-xs text-neutral-500 mb-3">Runs the AI on past claims your team already approved or declined, without telling it the answer, so you can see how often it agrees. Each claim uses AI credits.</p>
      <div className="flex items-center gap-2">
        <select value={n} onChange={(e) => setN(e.target.value)} className={selectCls} disabled={running} data-testid="wi-calibration-n">
          {[10, 25, 50, 100].map((v) => <option key={v} value={v}>{`${v} claims`}</option>)}
        </select>
        <button className={btnPrimary} onClick={run} disabled={busy || running || !ov.validated || ov.emergency_pause} data-testid="wi-calibration-run-btn">{running ? `Testing… ${job.done || 0}/${job.todo}` : "Run test"}</button>
      </div>
      {!ov.validated && <p className="text-[0.7rem] text-neutral-600 mt-2">Available after the historical database is validated.</p>}
      {cal.n > 0 && (
        <>
          <div className="grid grid-cols-2 gap-2 mt-4">
            <Stat label="Matches human" value={`${cal.agreement_pct}%`} sub={`${cal.agree} of ${cal.n} claims`} tone="green" testId="wi-cal-agreement" />
            <Stat label="Safe (match or review)" value={`${cal.safe_pct}%`} sub={`${cal.disagree} clear differences`} testId="wi-cal-safe" />
            <Stat label="Would auto-approve" value={cal.would_auto_approve} sub={`${cal.auto_approve_human_approved} were approved by your team`} tone="gold" testId="wi-cal-auto" />
            <Stat label="AI judgement matched" value={`${cal.ai_lean_agree_pct ?? 0}%`} sub={`AI leaned the same way · avg confidence ${cal.avg_confidence ?? "—"}`} testId="wi-cal-lean" />
          </div>
          <p className="text-[0.7rem] text-neutral-500 mt-2">“Matches human” only counts a suggestion of approval when confidence reaches {ov.settings.confidence_auto_approve}. Anything below goes to human review.</p>
          <Matrix m={cal.matrix} />
        </>
      )}
    </Card>
  );
}
